/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { useEffect, useState } from 'react';
import { Stripe } from '@stripe/stripe-js';

import {
  GatewayHookResponse,
  UseGatewayParams,
  AnyGatewayEnv,
  GatewayConfigResponse,
  AnyGatewayMode,
  StripeAccountType,
  PaymentServiceProcessorMetadata,
  TokenexFormConfigType,
  StripeFormConfigType,
} from '../@types';
import { ApiHosts } from '../constants';
import { getFormFieldMap } from '../components/maps/FormFieldMap';

import { loadTokenex } from './useTokenex';
import { loadStripe } from './useStripe';

// type FetchedPaymentServiceConfig = {
//   login: string;
//   publishableKey: string;
//   mode: AnyGatewayMode;
//   type: StripeAccountType;
//   metadata: PaymentServiceProcessorMetadata;
// };

type FetchGatewayParams = {
  env: AnyGatewayEnv;
  apiToken: string;
  propertyId: string;
  fallbackMode: AnyGatewayMode;
};

type FetchGatewayResponse =
  | {
      stripe: boolean;
      account: string;
      publishableKey: string;
      mode: AnyGatewayMode;
      type: StripeAccountType;
      metadata: PaymentServiceProcessorMetadata;
    }
  | {
      tokenex: boolean;
      mode: AnyGatewayMode;
    };

// function isEmptyResponseParameter(value: unknown) {
//   return !value || (Array.isArray(value) && value.length === 0);
// }

/**
 * @description Fetches the payment gateway configuration for the given property and environment.
 * @param {FetchGatewayParams} params - Parameters for fetching gateway config.
 * @returns {Promise<FetchGatewayResponse | null>} The gateway configuration or null if not found.
 */
const fetchGatewayConfig = async ({
  env = 'prod',
  apiToken = '',
  propertyId,
  fallbackMode,
}: FetchGatewayParams): Promise<FetchGatewayResponse | null> => {
  if (!propertyId) return null;
  const apiHost = ApiHosts[env].host;
  const authKeyUrl = `${apiHost}/payments-configuration/v1.0/property?propertyId=${propertyId}`;

  try {
    const response = await fetch(authKeyUrl, {
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Origin: ApiHosts[env].origin,
        'X-Property-Id': propertyId,
        ...(apiToken && { Authorization: `Bearer ${apiToken}` }),
      },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error('Network response was not ok');
    }

    const data: GatewayConfigResponse =
      response.status !== 404 ? await response.json() : {};

    const cardPaymentMethod = data?.payment_method?.find(
      pm => pm.name === 'card',
    );

    const cardProcessor = cardPaymentMethod
      ? data.processor?.find(
          p => cardPaymentMethod?.processors?.includes(p.name) && p.enabled,
        )
      : null;

    // This is only when card is not enabled for processing
    // This blocks component from loading on CCTab for no gateway users
    // comment out and then remove if all ok later
    // if (!cardProcessor && !isEmptyResponseParameter(data.processor))
    //   return null;

    return cardProcessor && cardProcessor.name === 'StripePlatformGateway'
      ? {
          stripe: true,
          account: cardProcessor.credentials.login!,
          publishableKey: cardProcessor.credentials.publishable_key!,
          mode: cardProcessor.mode.toLowerCase() as unknown as AnyGatewayMode,
          type:
            cardProcessor.name === 'StripePlatformGateway'
              ? 'platform'
              : ('direct' as unknown as StripeAccountType),
          metadata: cardProcessor.metadata,
        }
      : {
          tokenex: true,
          mode: (cardProcessor && cardProcessor.mode
            ? cardProcessor.mode.toLowerCase()
            : (
                fallbackMode || 'live'
              ).toLowerCase()) as unknown as AnyGatewayMode,
        };
  } catch (_error) {
    window.console.log('Can not load gateway config:', _error);
    return null;
  }
};

/**
 * @description React hook to load and manage the payment gateway provider (Stripe or Tokenex) and its configuration.
 * Handles loading state, error state, and field mapping.
 *
 * @param {UseGatewayParams} params - Parameters for gateway loading.
 * @returns {GatewayHookResponse} The gateway provider, options, error, and field map.
 */
export const useGateway = ({
  mode,
  apiToken,
  env,
  propertyId,
}: UseGatewayParams): GatewayHookResponse => {
  const [error, setLoadError] = useState<string>();
  const [provider, setProvider] = useState<
    TokenexFormConfigType['provider'] | StripeFormConfigType['provider'] | null
  >(null);
  const [config, setConfig] = useState<
    TokenexFormConfigType['options'] | StripeFormConfigType['options'] | null
  >(null);

  /**
   * TODO:
   * This counter is to re-request auth key before it expires,
   * added as dependency to the useEffect that loads auth key from backend.
   *
   * REMOVING: This is useless, since should trigger the whole component rerender
   */
  // const [expirationCounter, setExpirationCounter] = useState(0);
  // useEffect(() => {
  //   const interval = setInterval(
  //     () => {
  //       if (config) {
  //         setConfig(null);
  //         setProvider(null);
  //       }
  //       setExpirationCounter(c => c + 1);
  //     },
  //     15 * 60 * 1000,
  //   );
  //   return () => clearInterval(interval);
  // }, [expirationCounter, config]);

  const isLoaded = provider || error;

  useEffect(() => {
    /**
     * Do not load if loaded or error, host app should care about edge cases
     */
    if (isLoaded) {
      window.console.log('Gateway Loaded.');
      return;
    }
    fetchGatewayConfig({ apiToken, env, propertyId, fallbackMode: mode }).then(
      response => {
        if (!response) {
          setLoadError('Bad response from configuration service');
          window.console.error('Bad response from configuration service');
          return;
        }

        const isTokenex = 'tokenex' in response;
        const isStripe = 'stripe' in response;

        if (isStripe) {
          loadStripe({
            account: response.account,
            publishableKey: response.publishableKey,
            type: response.type,
          })
            .then(data => {
              setConfig(data?.options);
              setProvider(data?.provider as Stripe);
            })
            .catch(() => setLoadError('Can not load Stripe configuration.'));
        } else if (isTokenex) {
          loadTokenex({ mode: response.mode, env, apiToken })
            .then(data => {
              setConfig(data?.options);
              setProvider(data?.provider as string);
            })
            .catch(() => setLoadError('Can not load Tokenex configuration.'));
        } else {
          setLoadError('Can not find proper gateway configuration.');
          window.console.error('Can not find proper gateway configuration.');
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken, env, propertyId]);

  return {
    loaded: !!provider,
    error,
    provider,
    options: config || null,
    fieldMap: provider ? getFormFieldMap(provider === 'tokenex') : null,
  };
};
